import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { translations } from "./data";

// Lazy-loaded components for optimal initial bundle size and performance
const Home = lazy(() => import("./pages/Home"));
const GalleryLightbox = lazy(() => import("./components/GalleryLightbox"));
const NotFound = lazy(() => import("./pages/ErrorPages").then(m => ({ default: m.NotFound })));
const Forbidden = lazy(() => import("./pages/ErrorPages").then(m => ({ default: m.Forbidden })));

// Hash Redirect & Scroll Handler Component
function HashRedirectAndScroll({ setActiveSection, isScrollingRef }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathMap = {
      "/about": "about",
      "/newalbert": "newalbert",
      "/ai-knowledge-management": "ai-knowledge",
      "/contact": "contact",
    };

    const targetHash = pathMap[location.pathname];
    if (targetHash) {
      // Redirect to home with hash
      navigate(`/#${targetHash}`, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

// Comprehensive Dynamic SEO Metadata & Schema JSON-LD Component
function SEOMetadata({ lang, cmsPages, activeSection }) {
  useEffect(() => {
    // Map active section to CMS page slugs
    let pageSlug = "page-home";
    if (activeSection === "about") pageSlug = "page-about";
    else if (activeSection === "newalbert") pageSlug = "page-newalbert";
    else if (activeSection === "ai-knowledge") pageSlug = "page-ai-knowledge-management";
    else if (activeSection === "contact") pageSlug = "page-contact";

    // Find page metadata from CMS
    const cmsPage = cmsPages.find((p) => p.slug === pageSlug || p.slug?.[lang] === pageSlug);

    let title = "";
    let description = "";

    if (cmsPage) {
      title = cmsPage.meta_title?.[lang] || cmsPage.meta_title || "";
      description = cmsPage.meta_description?.[lang] || cmsPage.meta_description || "";
    }

    // Fallbacks if CMS is loading or missing
    if (!title) {
      if (activeSection === "home" || activeSection === "what-we-do" || activeSection === "gallery") {
        title = lang === "en" 
          ? "Albertsen Group - Next-Gen Climate & Sustainability Software" 
          : "Albertsen Group - Næste Generations Klima- og Bæredygtighedssoftware";
        description = lang === "en" 
          ? "Albertsen Group makes next-generation collaboration software and services for Climate research and Sustainability. Your trusted partner in academic research."
          : "Albertsen Group skaber næste generations samarbejdssoftware og tjenester til klimaforskning og bæredygtighed. Din betroede partner i akademisk forskning.";
      } else if (activeSection === "about") {
        title = lang === "en" ? "About Us - Albertsen Group" : "Om Os - Albertsen Group";
        description = lang === "en"
          ? "Learn more about Albertsen Group, LLC, our mission in academic AI platforms, European headquarters in Denmark, and research partnerships."
          : "Læs mere om Albertsen Group, LLC, vores mission inden for akademiske AI-platforme, europæisk hovedkvarter i Danmark og forskningspartnerskaber.";
      } else if (activeSection === "newalbert") {
        title = lang === "en" ? "NewAlbert AI - Research Partner Solution | Albertsen Group" : "NewAlbert AI - Forskningspartner Løsning | Albertsen Group";
        description = lang === "en"
          ? "Discover NewAlbert, an AI-driven Knowledge Management system powered by RAG technology and AI reasoning for academic research."
          : "Oplev NewAlbert, et AI-drevet vidensstyringssystem baseret på RAG-teknologi og AI-ræsonnement til akademisk forskning.";
      } else if (activeSection === "ai-knowledge") {
        title = lang === "en" ? "AI Knowledge Management - Albertsen Group" : "AI Vidensstyring - Albertsen Group";
        description = lang === "en"
          ? "Revolutionizing how institutions interact with large document repositories using semantic search, NLP, and AI synthesis."
          : "Revolutionerer måden institutioner interagerer med store dokumentlagre ved hjælp af semantisk søgning, NLP og AI-syntese.";
      } else if (activeSection === "contact") {
        title = lang === "en" ? "Contact Us - Albertsen Group" : "Kontakt Os - Albertsen Group";
        description = lang === "en"
          ? "Get in touch with Albertsen Group in Denmark or the USA. Contact us regarding AI knowledge management platforms and academic research collaboration."
          : "Kontakt Albertsen Group i Danmark eller USA. Kontakt os vedrørende AI-vidensstyringsplatforme og akademisk forskningssamarbejde.";
      } else {
        title = "Albertsen Group - AI-Powered Knowledge Management Platforms";
        description = "Albertsen Group, LLC operates at the intersection of academic rigor and next-generation artificial intelligence.";
      }
    }

    // Set document title & HTML lang
    document.title = title;
    document.documentElement.lang = lang;

    // Helper function to update or create meta tags
    const updateMeta = (selector, attrName, attrVal, content) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attrName, attrVal);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    // Helper function to update or create link tags
    const updateLink = (rel, href) => {
      let tag = document.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", href);
    };

    // Live domain canonical base
    const liveDomain = "https://albert-grp.com";
    const currentCanonicalUrl = activeSection === "home" ? `${liveDomain}/` : `${liveDomain}/#${activeSection}`;
    const ogImageUrl = "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/albertsen-group-logo-rez.png";

    // Meta Description & Canonical
    updateMeta('meta[name="description"]', 'name', 'description', description);
    updateLink('canonical', currentCanonicalUrl);

    // Staging Protection Notice
    updateMeta('meta[name="robots"]', 'name', 'robots', 'noindex, nofollow');

    // Open Graph Metadata
    updateMeta('meta[property="og:title"]', 'property', 'og:title', title);
    updateMeta('meta[property="og:description"]', 'property', 'og:description', description);
    updateMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    updateMeta('meta[property="og:url"]', 'property', 'og:url', currentCanonicalUrl);
    updateMeta('meta[property="og:image"]', 'property', 'og:image', ogImageUrl);
    updateMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'Albertsen Group');
    updateMeta('meta[property="og:locale"]', 'property', 'og:locale', lang === 'da' ? 'da_DK' : 'en_US');

    // Twitter Card Metadata
    updateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    updateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    updateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImageUrl);

    // Schema JSON-LD Injection
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${liveDomain}/#organization`,
          "name": "Albertsen Group, LLC",
          "url": liveDomain,
          "logo": {
            "@type": "ImageObject",
            "url": ogImageUrl
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Dr. Olgas Vej 30",
            "addressLocality": "Frederiksberg",
            "postalCode": "2000",
            "addressCountry": "DK"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+45 20 50 27 88",
              "contactType": "customer support",
              "areaServed": "DK",
              "availableLanguage": ["Danish", "English"]
            },
            {
              "@type": "ContactPoint",
              "telephone": "+1-617-487-2008",
              "contactType": "headquarters",
              "areaServed": "US",
              "availableLanguage": ["English"]
            }
          ],
          "email": "Info@albert-grp.com"
        },
        {
          "@type": "WebSite",
          "@id": `${liveDomain}/#website`,
          "url": liveDomain,
          "name": "Albertsen Group",
          "publisher": {
            "@id": `${liveDomain}/#organization`
          },
          "inLanguage": lang === "da" ? "da-DK" : "en-US"
        },
        {
          "@type": "WebPage",
          "@id": currentCanonicalUrl,
          "url": currentCanonicalUrl,
          "name": title,
          "description": description,
          "isPartOf": {
            "@id": `${liveDomain}/#website`
          },
          "inLanguage": lang === "da" ? "da-DK" : "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${currentCanonicalUrl}#breadcrumb`,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": lang === "da" ? "Forside" : "Home",
              "item": liveDomain
            },
            ...(activeSection !== "home" ? [{
              "@type": "ListItem",
              "position": 2,
              "name": title.split(" - ")[0],
              "item": currentCanonicalUrl
            }] : [])
          ]
        }
      ]
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.setAttribute("type", "application/ld+json");
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schemaData);

  }, [activeSection, lang, cmsPages]);

  return null;
}

function AppContent() {
  const [lang, setLang] = useState("en");
  const [menuItems, setMenuItems] = useState([]);
  const [cmsPages, setCmsPages] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [activeSection, setActiveSection] = useState("home");

  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Lightbox State
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Translation helper
  const t = (key) => {
    const keys = key.split(".");
    let value = translations[lang];
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        return key; // Fallback to key itself
      }
    }
    return value;
  };

  // Fetch CMS Content on mount
  useEffect(() => {
    const fetchCMS = async () => {
      try {
        const cacheBuster = `?_t=${Date.now()}`;
        const [menuRes, pagesRes, galleryRes] = await Promise.all([
          fetch(`https://api.dev.auxo.dk/public/content/menu_items${cacheBuster}`),
          fetch(`https://api.dev.auxo.dk/public/content/pages${cacheBuster}`),
          fetch(`https://api.dev.auxo.dk/public/content/gallery${cacheBuster}`),
        ]);

        if (menuRes.ok) {
          const data = await menuRes.json();
          setMenuItems(data);
        }
        if (pagesRes.ok) {
          const data = await pagesRes.json();
          setCmsPages(data);
        }
        if (galleryRes.ok) {
          const data = await galleryRes.json();
          setGalleries(data);
        }
      } catch (error) {
        console.error("Error fetching CMS content:", error);
      }
    };

    fetchCMS();
  }, []);

  // Fallback menu items if CMS fetch is loading or empty
  const activeMenuItems = menuItems.length > 0 ? menuItems : [
    { label: { en: "Home", da: "Forside" }, slug: "home", order: 1, parent_slug: "" },
    { label: { en: "About Us", da: "Om Os" }, slug: "about", order: 2, parent_slug: "" },
    { label: { en: "Solutions", da: "Løsninger" }, slug: "solutions", order: 3, parent_slug: "" },
    { label: { en: "NewAlbert AI", da: "NewAlbert AI" }, slug: "newalbert", order: 4, parent_slug: "solutions" },
    { label: { en: "AI Knowledge Management", da: "AI Vidensstyring" }, slug: "ai-knowledge-management", order: 5, parent_slug: "solutions" },
    { label: { en: "Contact", da: "Kontakt" }, slug: "contact", order: 6, parent_slug: "" }
  ];

  // Fallback galleries if CMS fetch is loading or empty
  const activeGalleries = galleries.length > 0 ? galleries : [
    {
      id: "fallback-1",
      title: { en: "Climate & Technology Showcase", da: "Klima- og teknologiudstilling" },
      description: { en: "Visual representation of our core domains: Nyhavn, wind energy, artificial intelligence, and global collaboration.", da: "Visuel repræsentation af vores kerneområder: Nyhavn, vindenergi, kunstig intelligens og globalt samarbejde." },
      slug: "home-hero",
      images: [
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/copenhagen-nyhavn.jpg",
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/shutterstock-2750147293.jpg",
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/ai-representation.jpg",
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/global-work.jpg"
      ]
    },
    {
      id: "fallback-2",
      title: { en: "Academic & Research Environments", da: "Akademiske- og forskningsmiljøer" },
      description: { en: "Modern Scandinavian university campuses and research centers partnering with Albertsen Group.", da: "Moderne skandinaviske universitetsområder og forskningscentre, der samarbejder med Albertsen Group." },
      slug: "academic-collaboration",
      images: [
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/denmark-flag-campus.jpg",
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/university-reflection.jpg"
      ]
    },
    {
      id: "fallback-3",
      title: { en: "AI & Knowledge Management Visuals", da: "AI- og vidensstyringsvisualiseringer" },
      description: { en: "Conceptual representations of AI knowledge graphs and research tools.", da: "Konceptuelle repræsentationer af AI-vidensgrafer og forskningsværktøjer." },
      slug: "ai-solutions",
      images: [
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/scrabble-news-ai.jpg",
        "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/einstein-globe.jpg"
      ]
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      isScrollingRef.current = true;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
      window.history.replaceState(null, null, `#${sectionId}`);

      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  };

  const handleImageClick = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };

  const handlePrevImage = () => {
    setLightboxIndex((prev) => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setLightboxIndex((prev) => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="flex flex-col min-h-screen bg-zinc-100 text-zinc-900 antialiased font-sans">
      <HashRedirectAndScroll setActiveSection={setActiveSection} isScrollingRef={isScrollingRef} />
      <SEOMetadata lang={lang} cmsPages={cmsPages} activeSection={activeSection} />

      <Navbar 
        lang={lang} 
        setLang={setLang} 
        t={t} 
        menuItems={activeMenuItems} 
        activeSection={activeSection}
        scrollToSection={scrollToSection}
      />

      <div className="flex-grow">
        <Suspense fallback={<div className="min-h-screen bg-zinc-100 flex items-center justify-center text-zinc-400 text-xs font-semibold uppercase tracking-widest">Loading...</div>}>
          <Routes>
            <Route
              path="/"
              element={
                <Home
                  lang={lang}
                  t={t}
                  galleries={activeGalleries}
                  onImageClick={handleImageClick}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  scrollToSection={scrollToSection}
                  isScrollingRef={isScrollingRef}
                />
              }
            />
            <Route path="/403" element={<Forbidden t={t} />} />
            <Route path="*" element={<NotFound t={t} />} />
          </Routes>
        </Suspense>
      </div>

      {/* Global Lightbox */}
      <Suspense fallback={null}>
        <GalleryLightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      </Suspense>
    </div>
  );
}

export default function App() {
  return <AppContent />;
}
